import { cn } from '@veraclins-dev/utils';

import { List } from '../ui/list';
import { ListItem } from '../ui/list-item';
import { Typography } from '../ui/typography';

interface ErrorListProps {
  errors?: string[];
  id?: string;
  /**
   * Announce the errors as soon as they appear. Set it for form-level errors
   * that belong to no field. Field errors leave it off, because the field
   * reads them through `aria-describedby` when it is focused.
   */
  urgent?: boolean;
}

/**
 * Renders a field's validation messages as an accessible live region.
 * The region stays mounted while empty, because screen readers usually skip
 * a live region that appears already filled. Keep it rendered and change
 * `errors` instead of mounting it conditionally.
 * @category forms
 */
export const ErrorList = ({ errors, id, urgent }: ErrorListProps) => {
  const hasErrors = Boolean(errors?.length);

  return (
    <List
      id={id}
      role={urgent ? 'alert' : undefined}
      aria-live={urgent ? undefined : 'polite'}
      // Out of flow while empty, so the idle region adds no gap or margin.
      className={cn(
        'list-none pl-0 text-danger',
        hasErrors ? '-mt-1' : 'absolute',
      )}
    >
      {errors?.map((error, index) => (
        <ListItem key={index}>
          <Typography className="text-danger" variant="caption">
            {error}
          </Typography>
        </ListItem>
      ))}
    </List>
  );
};
